import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── Personalization: Contacts (Recipient Profiles) ────────────────────

export type RecipientContact = {
  id: string;
  name: string;
  phone: string;
  email: string;
  attributes: Record<string, string>;
  call_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
};

export type RecipientContactInput = {
  name: string;
  phone: string;
  email?: string;
  attributes?: Record<string, string>;
  /** When set, server links the new contact to this campaign on create (`POST /api/contacts`). */
  campaign_id?: string;
};

function mapApiContact(raw: any): RecipientContact {
  return {
    id: String(raw.id ?? raw.recipient_id ?? ''),
    name: String(raw.name ?? ''),
    phone: String(raw.phone ?? ''),
    email: String(raw.email ?? ''),
    attributes: (raw.attributes && typeof raw.attributes === 'object') ? raw.attributes : {},
    call_count: Number(raw.call_count ?? 0),
    first_seen_at: raw.first_seen_at ?? null,
    last_seen_at: raw.last_seen_at ?? null,
  };
}

export type ContactEstimateStatus = 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired';

export type ContactEstimate = {
  id: string;
  contact_id: string;
  status: ContactEstimateStatus;
  sent_date: string | null;
  follow_up_count: number;
  next_follow_up_date: string | null;
  estimate_amount: string | null;
  service_type: string | null;
  estimate_date: string | null;
  expiration_date: string | null;
  inspector_name: string | null;
  notes: string | null;
};

/** `GET /api/contacts/{id}/estimate` — BOOSTT-3166 estimate lifecycle. Returns null when the contact has no estimate on file (backend 404s). */
export async function fetchContactEstimate(contactId: string, axiosInstance?: AxiosInstance): Promise<ContactEstimate | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  try {
    const response = await client.get<ContactEstimate>(`${apiConfig.voiceEndpoints.contacts}/${contactId}/estimate`);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** Same {attestor, disclosure_text, captured_at} shape documented on
 * CampaignCreate.consent_attestation's field description — the two independent
 * references to this exact shape (there and here in the original F7 spec)
 * corroborate it, even though this endpoint's own OpenAPI schema is untyped. */
export type ContactConsentEvidence = {
  attestor: string | null;
  disclosure_text: string | null;
  captured_at: string | null;
};

export type ContactConsentChannel = {
  opted_out: boolean;
  opted_out_at: string | null;
  opted_in_at: string | null;
  source: string | null;
  /** F7 (2026-08-19) — optional and additive: degrades to "no evidence
   * recorded" if the backend doesn't populate it yet. */
  evidence?: ContactConsentEvidence[];
};

export type ContactConsent = {
  sms?: ContactConsentChannel;
  voice?: ContactConsentChannel;
};

/** `GET /api/contacts/{id}/consent` — BOOSTT-3176 per-channel opt-out status. */
export async function fetchContactConsent(contactId: string, axiosInstance?: AxiosInstance): Promise<ContactConsent> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<ContactConsent>(`${apiConfig.voiceEndpoints.contacts}/${contactId}/consent`);
  return response.data;
}

export type ContactSmsActivity = {
  id: string;
  status: string;
  sent_at: string | null;
  created_at: string | null;
  template: string | null;
};

export type ContactVoicemailActivity = {
  id: string;
  status: string;
  duration_seconds: number | null;
  transcript: string | null;
  recording_url: string | null;
  created_at: string | null;
};

export type ContactActivity = {
  sms: ContactSmsActivity[];
  voicemails: ContactVoicemailActivity[];
};

/** `GET /api/contacts/{id}/activity` — BOOSTT-3177/3178 SMS + voicemail audit trail for this contact. */
export async function fetchContactActivity(contactId: string, axiosInstance?: AxiosInstance): Promise<ContactActivity> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<ContactActivity>(`${apiConfig.voiceEndpoints.contacts}/${contactId}/activity`);
  return response.data;
}

/** `GET /api/contacts` — list contacts with optional search, pagination, and `campaign_id` filter. */
export async function fetchContacts(
  params: { search?: string; skip?: number; limit?: number; campaign_id?: string } = {},
  axiosInstance?: AxiosInstance
): Promise<RecipientContact[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  // Previously only search/skip/limit were passed; campaign-scoped lists were not supported in the client:
  // const response = await client.get<any>(apiConfig.voiceEndpoints.contacts, {
  //   params: { search: params.search, skip: params.skip, limit: params.limit },
  // });
  const response = await client.get<any>(apiConfig.voiceEndpoints.contacts, { params });
  const list = Array.isArray(response.data) ? response.data : response.data?.contacts ?? response.data?.items ?? [];
  return list.map(mapApiContact);
}

export async function fetchContactsPaginated(
  params: { search?: string; skip?: number; limit?: number; campaign_id?: string } = {},
  axiosInstance?: AxiosInstance
): Promise<{ items: RecipientContact[]; total: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(apiConfig.voiceEndpoints.contacts, { params });
  const raw = Array.isArray(response.data) ? response.data : response.data?.items ?? response.data?.contacts ?? [];
  const total: number = response.data?.total ?? raw.length;
  return { items: raw.map(mapApiContact), total };
}

/** `POST /api/contacts` — create a new contact. */
export async function createContact(
  input: RecipientContactInput,
  axiosInstance?: AxiosInstance
): Promise<RecipientContact> {
  const client = axiosInstance || getVoiceFallbackClient();
  // const response = await client.post<any>(apiConfig.voiceEndpoints.contacts, input);
  const body: Record<string, unknown> = {
    name: input.name,
    phone: input.phone,
    ...(input.email !== undefined && input.email !== '' ? { email: input.email } : {}),
    ...(input.attributes && Object.keys(input.attributes).length ? { attributes: input.attributes } : {}),
    ...(input.campaign_id ? { campaign_id: input.campaign_id } : {}),
  };
  const response = await client.post<any>(apiConfig.voiceEndpoints.contacts, body);
  return mapApiContact(response.data);
}

/** `POST /api/contacts/{contact_id}/campaigns/{campaign_id}` — link an existing contact to a campaign. */
export async function linkContactToCampaign(
  campaignId: string,
  contactId: string,
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.contacts}/${contactId}/campaigns/${campaignId}`;
  await client.post(url, {});
  return { ok: true };
}

/** `DELETE /api/contacts/{contact_id}/campaigns/{campaign_id}` — remove contact from campaign without deleting the contact record. */
export async function unlinkContactFromCampaign(
  contactId: string,
  campaignId: string,
  axiosInstance?: AxiosInstance
): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.contacts}/${contactId}/campaigns/${campaignId}`;
  const response = await client.delete(url);
  return response.status === 200 || response.status === 204;
}

/** `PATCH /api/contacts/{id}` — update a contact's name, phone, email and attributes. */
export async function updateContact(
  id: string,
  input: RecipientContactInput,
  axiosInstance?: AxiosInstance
): Promise<RecipientContact> {
  const client = axiosInstance || getVoiceFallbackClient();
  const { campaign_id: _omit, ...rest } = input;
  void _omit;
  const response = await client.patch<any>(`${apiConfig.voiceEndpoints.contacts}/${id}`, rest);
  const raw = response.data?.contact ?? response.data?.data ?? response.data;
  return mapApiContact(raw);
}

/** `DELETE /api/contacts/{id}` — delete a contact. */
export async function deleteContact(id: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`${apiConfig.voiceEndpoints.contacts}/${id}`);
  return response.status === 200 || response.status === 204;
}

/** `POST /api/contacts/bulk` — bulk upload/upsert contacts. */
export async function bulkUploadContacts(
  data: { contacts: Array<{ name: string; phone: string; email?: string; attributes?: Record<string, any> }>; campaign_id?: string },
  axiosInstance?: AxiosInstance
): Promise<{ created: number; updated: number; total: number; errors: any[]; error_count: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.contacts}/bulk`;
  const response = await client.post(url, data);
  return response.data;
}

/** `POST /api/contacts/bulk-delete` — mass delete contacts. */
export async function bulkDeleteContacts(contactIds: string[], axiosInstance?: AxiosInstance): Promise<{ deleted: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.contacts}/bulk-delete`;
  const response = await client.post<{ deleted: number }>(url, { contact_ids: contactIds });
  return response.data;
}
